import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CrispService, { type CrispUser } from "../services/crispService";

import useUserStore from "@/features/auth/store/useUserStore";

export function useCrispChat() {
  const { i18n } = useTranslation(["common", "chat"]);
  const { user } = useUserStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [embedUrl, setEmbedUrl] = useState<string>("");
  const crispService = CrispService.getInstance();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Initialize Crisp
  useEffect(() => {
    const initializeCrisp = async () => {
      try {
        setIsLoading(true);

        // Prepare user data
        const userData: CrispUser | undefined = user
          ? {
              id: user.id,
              nickname:
                user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User",
              avatar: user.avatarUrl || undefined,
            }
          : undefined;

        // Generate embed URL with user data
        const url = crispService.getEmbedUrl(i18n.language, userData);

        setEmbedUrl(url);
        setIsConnected(true);
      } catch (error) {
        console.error("Failed to initialize Crisp:", error);
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCrisp();
  }, [user, i18n.language]);

  // Update embed URL when user changes
  useEffect(() => {
    if (user && isConnected) {
      const userData: CrispUser = {
        id: user.id,
        nickname:
          user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User",
        avatar: user.avatarUrl || undefined,
      };

      const url = crispService.getEmbedUrl(i18n.language, userData);

      setEmbedUrl(url);
    }
  }, [user, i18n.language, isConnected]);

  return {
    // State
    isLoading,
    isConnected,
    embedUrl,

    // Refs
    iframeRef,

    // Current user info
    currentUser: user
      ? {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatarUrl: user.avatarUrl,
        }
      : null,
  };
}
